import { useStacklineTheme } from '@stackline/ui';
import React from 'react';
import { TopicFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  useTopicBuilderActions,
  useTopicBuilderFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import CheckboxItem, {
  CheckboxDimensions
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import {
  DisabledCheckbox,
  SlDisabledExcludeSelectedCheckbox,
  SlExcludeSelectedCheckbox,
  SlSelectedCheckbox
} from 'src/components/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';

interface SubcategorySidebarFilterProps extends GenericSidebarFilterProps {
  readOnly?: boolean;
}

const ICON_STYLES = {
  width: CheckboxDimensions.DEFAULT_WIDTH,
  height: CheckboxDimensions.DEFAULT_HEIGHT
};

const SubcategorySidebarFilter = (props: SubcategorySidebarFilterProps) => {
  const theme = useStacklineTheme();

  const { filters } = useTopicBuilderFilters();
  const { setFilters } = useTopicBuilderActions();

  const includedCategories = filters[TopicFilter.IncludedCategoryIds];
  const excludedCategories = filters[TopicFilter.ExcludedCategoryIds];

  // Determines if the user is including or excluding categories
  const isIncludingCategories = includedCategories.length >= 0 && excludedCategories.length === 0;

  // Determines which categories are selected based on the include/exclude mode.
  // We will use these to determine which subcategories to render in the renderItems functions.
  const selectedCategories = isIncludingCategories ? includedCategories : excludedCategories;

  // Filters the subcategories based on the selected subcategories
  const includedSubcategories = filters[TopicFilter.IncludedSubCategoryIds];
  const excludedSubcategories = filters[TopicFilter.ExcludedSubCategoryIds];

  // Determines if the user is including or excluding subcategories
  const isIncludingSubcategories = includedSubcategories.length >= 0 && excludedSubcategories.length === 0;

  // Determines which subcategories are selected based on the include/exclude mode.
  const selectedSubcategories = isIncludingSubcategories ? includedSubcategories : excludedSubcategories;

  const shouldRender = selectedSubcategories?.length > 0 || !props.readOnly;

  // The options to render for the subcategory filter. See renderItems for details on determining which subcategories to render.
  const subcategoryOptions = useAppSelector((state) =>
    state.subCategories.map((subcategory) => ({
      ...subcategory,
      selected: selectedSubcategories.some((selectedSubcategory) => String(selectedSubcategory) === subcategory.id)
    }))
  );

  /**
   * Search input for subcategories
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const [toggleMode, setToggleMode] = React.useState<'include' | 'exclude'>(
    isIncludingSubcategories ? 'include' : 'exclude'
  );

  React.useEffect(() => {
    setToggleMode(isIncludingSubcategories ? 'include' : 'exclude');
  }, [isIncludingSubcategories]);

  /**
   * Handles the selection of an subcategory
   */
  const handleItemSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const id = Number(value);
      switch (toggleMode) {
        case 'include':
          setFilters(({ toggle }) => toggle({ key: TopicFilter.IncludedSubCategoryIds, value: id }));
          break;
        case 'exclude':
          setFilters(({ toggle }) => toggle({ key: TopicFilter.ExcludedSubCategoryIds, value: id }));
          break;
        default:
      }
    },
    [toggleMode, setFilters]
  );

  const getIcon = React.useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        if (props.readOnly) {
          return <DisabledCheckbox style={ICON_STYLES} />;
        } else {
          return <SlSelectedCheckbox style={ICON_STYLES} />;
        }
      }

      case 'exclude': {
        if (props.readOnly) {
          return <SlDisabledExcludeSelectedCheckbox style={ICON_STYLES} />;
        } else {
          return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
        }
      }
      default:
        return null;
    }
  }, [toggleMode, props.readOnly]);

  /**
   * Renders the list of items as checkboxes
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {subcategoryOptions
          ?.filter(
            (subcategory) =>
              subcategory.name.toLowerCase().includes(currentInput.toLowerCase()) &&
              (selectedCategories.length === 0 ||
                selectedCategories.includes(subcategory.parentCategoryId) ||
                subcategory.selected)
            // And the parent category is selected if any are selected
          )
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((subcategory, index) => (
            <CheckboxItem
              disabled={props.readOnly}
              onClick={handleItemSelect}
              value={subcategory.id}
              key={index}
              label={subcategory.name}
              theme={theme}
              checked={subcategory.selected}
              checkedIcon={getIcon()}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [currentInput, theme, handleItemSelect, getIcon, selectedCategories, subcategoryOptions, props.readOnly]);

  /**
   * Handles the change of the input
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  const handleClear = () => {
    switch (toggleMode) {
      case 'include':
        setFilters(({ clear }) => clear({ key: TopicFilter.IncludedSubCategoryIds }));
        break;
      case 'exclude':
        setFilters(({ clear }) => clear({ key: TopicFilter.ExcludedSubCategoryIds }));
        break;
      default:
    }

    setCurrentInput('');
  };

  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);
    switch (toggle) {
      case 'include':
        setFilters(({ replace }) =>
          replace({ source: TopicFilter.ExcludedSubCategoryIds, target: TopicFilter.IncludedSubCategoryIds })
        );
        break;
      case 'exclude':
        setFilters(({ replace }) =>
          replace({ source: TopicFilter.IncludedSubCategoryIds, target: TopicFilter.ExcludedSubCategoryIds })
        );
        break;
      default:
    }
  };

  return (
    <>
      {shouldRender && (
        <GenericSidebarFilter
          {...props}
          includeInput={!props.readOnly}
          includeClear={selectedSubcategories.length > 0 && !props.readOnly}
          includeToggles={!props.readOnly}
          renderItems={renderItems}
          handleClear={handleClear}
          handleToggle={handleToggle}
          toggleMode={toggleMode}
          sidebarInputProps={{
            ...props.sidebarInputProps,
            value: currentInput,
            onChange: handleChange
          }}
        />
      )}
    </>
  );
};

export default SubcategorySidebarFilter;
